import { motion } from "framer-motion";
import { Scissors, Palette, Sparkles, Heart, Wand2, Crown, Layers, Flower2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SITE } from "@/constants/site";
import { FadeUp } from "@/components/animations/Reveal";

const GROUP_ICONS = [Scissors, Palette, Sparkles, Layers, Wand2, Crown, Flower2];

export default function Pricing() {
    const { t } = useLanguage();

    return (
        <section id="pricing" className="relative py-16 md:py-36 overflow-hidden" data-testid="pricing">
            {/* Salon interior background */}
            <div className="absolute inset-0 z-0">
                <img
                    src={SITE.images.gallery[2]}
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                    style={{ filter: "brightness(0.95) saturate(1.05)" }}
                />
                {/* Warm cream wash — like the price list images */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(248,245,240,0.92) 0%, rgba(248,245,240,0.88) 50%, rgba(248,245,240,0.92) 100%)",
                    }}
                />
                <div className="absolute inset-0 noise opacity-50" />
            </div>

            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10">
                {/* Chapter row */}
                <div className="flex items-center gap-4 mb-10">
                    <span className="chapter-num text-lg">{t.pricing.chapter}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                    <span className="eyebrow">{t.pricing.eyebrow}</span>
                </div>

                {/* Editorial "Price list" title */}
                <FadeUp>
                    <div className="relative text-center mb-6 md:mb-10">
                        <h2
                            className="font-display text-ink leading-[0.9] tracking-[0.04em] md:tracking-[0.08em] text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9rem]"
                            data-testid="price-list-title"
                        >
                            PRICE
                        </h2>
                        <div
                            className="absolute left-1/2 -translate-x-1/2 top-[42%] md:top-[48%] font-display italic text-gold-deep text-[3rem] sm:text-6xl md:text-7xl lg:text-8xl rotate-[-6deg]"
                            style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                            list
                        </div>
                    </div>
                </FadeUp>

                <FadeUp delay={0.1} className="text-center mb-16 md:mb-20">
                    <div className="inline-flex items-center gap-4">
                        <span className="h-[1px] w-10 bg-gold/60" />
                        <p className="text-ink/70 text-sm md:text-base italic font-display max-w-xl">
                            {t.pricing.sub}
                        </p>
                        <span className="h-[1px] w-10 bg-gold/60" />
                    </div>
                </FadeUp>

                {/* Groups grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
                    {t.pricing.groups.map((group, gi) => {
                        const Icon = GROUP_ICONS[gi] || Scissors;
                        return (
                            <FadeUp key={group.title} delay={0.05 * gi}>
                                <article
                                    className="group h-full relative bg-white/85 backdrop-blur-md rounded-2xl border border-gold/25 overflow-hidden shadow-[0_30px_60px_-30px_rgba(100,70,20,0.25)] hover:border-gold/50 hover:bg-white/92 transition-all duration-500"
                                    data-testid={`price-group-${gi}`}
                                >
                                    {/* Group header */}
                                    <div className="flex items-center gap-3 md:gap-4 px-5 md:px-8 py-4 md:py-6 border-b border-gold/15">
                                        <span className="inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full border border-gold/50 text-gold-deep bg-cream/60 shrink-0">
                                            <Icon className="w-4 h-4" />
                                        </span>
                                        <h3 className="font-display italic text-lg md:text-2xl text-ink flex-1 min-w-0 leading-tight">
                                            {group.title}
                                        </h3>
                                        <span className="hidden sm:inline text-[0.6rem] tracking-[0.35em] uppercase text-gold-deep">
                                            0{gi + 1}
                                        </span>
                                    </div>

                                    {/* Rows */}
                                    <ul className="divide-y divide-gold/12">
                                        {group.items.map((row, i) => (
                                            <motion.li
                                                key={row.s + i}
                                                initial={{ opacity: 0, y: 10 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, amount: 0.2 }}
                                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                                className="group/row flex items-start justify-between gap-3 md:gap-5 px-5 md:px-8 py-4 md:py-5 hover:bg-cream/40 transition-colors"
                                                data-testid={`price-row-${gi}-${i}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-sans text-ink text-sm md:text-base leading-snug">
                                                        {row.s}
                                                    </div>
                                                    {row.n && (
                                                        <div className="mt-1 font-display italic text-[0.72rem] md:text-sm text-ink/55 leading-snug">
                                                            {row.n}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="shrink-0 font-display italic text-sm md:text-lg text-ink/85 group-hover/row:text-gold whitespace-nowrap transition-colors">
                                                    {row.p}
                                                </div>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </article>
                            </FadeUp>
                        );
                    })}
                </div>

                {/* Notice */}
                <FadeUp delay={0.1} className="mt-14">
                    <div className="bg-ink text-cream rounded-2xl p-7 md:p-9 relative overflow-hidden">
                        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-gold/25" />
                        <div className="absolute -bottom-20 -left-16 w-64 h-64 rounded-full border border-gold/15" />
                        <div className="relative flex items-start gap-5">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gold/15 border border-gold/40 shrink-0">
                                <Heart className="w-4 h-4 text-gold" />
                            </span>
                            <p className="text-cream/90 text-sm md:text-base leading-relaxed max-w-3xl">
                                {t.pricing.note}
                            </p>
                        </div>
                    </div>
                </FadeUp>

                {/* Signature footer */}
                <FadeUp delay={0.15}>
                    <div className="mt-10 text-center">
                        <span className="font-display italic text-gold-deep tracking-[0.1em]">
                            frizerka.de
                        </span>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
